import { useState } from 'react';

import {
  AsyncSelect,
  type AsyncSelectLoadOptions,
  type IAsyncSelectProps,
} from '@procurify/ui'
import { get, isUndefined } from 'lodash'

import {
  type DepartmentsAPIGetLocationsParamsSchema,
  DEPARTMENTS_URL,
} from '@webapp/api/DepartmentsAPI'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { DepartmentSelectSelectStyled } from './styles/DepartmentSelectStyled'
import { type Department } from '../../models/Department'
import { ApiClient } from '../../services/api'

export type IDepartmentSelectProps<IsMulti extends boolean = false> =
  IAsyncSelectProps<Department, IsMulti> & {
    location?: number | string
    pageSize?: number | 'all'
    params?: DepartmentsAPIGetLocationsParamsSchema
  }

interface GroupOptions {
  groupIsInLastPage: boolean
  groupId: number | ''
}

type GroupByFn = (option: Department) => GroupOptions

const groupOptions = (options: Department[], groupByFn: GroupByFn) => {
  const groupMap = {}
  const groupedChoices = []
  let finalChoices = []

  options.forEach((option) => {
    const { groupIsInLastPage, groupId } = groupByFn(option)

    let groupIndex

    if (groupIsInLastPage === true) {
      finalChoices = finalChoices.concat(option)
    } else {
      if (isUndefined(groupMap[groupId])) {
        groupIndex = groupedChoices.length

        groupMap[groupId] = groupIndex

        groupedChoices[groupIndex] = []
      }

      groupedChoices[groupMap[groupId]].push(option)
    }
  })

  groupedChoices.forEach((groupedChoice) => {
    finalChoices = finalChoices.concat({
      label: get(groupedChoice[0], 'branch.name'),
      options: groupedChoice,
    })
  })

  options = finalChoices

  return finalChoices
}

export const DepartmentSelect = <IsMulti extends boolean = false>(
  props: IDepartmentSelectProps<IsMulti>
) => {
  /**
   * The destructuring is here instead of in the function arguments
   * because there is root issue with Typescript that breaks Intellisense.
   *
   * The root issue is that writing:
   *
   * export interface IDepartmentSelectProps extends Omit<IAsyncSelectProps<Department>, 'pageSize'> {
   *   pageSize?: number | 'all'
   * }
   *
   *
   * breaks Intellisense and when using `<DepartmentSelect />`, none of the props populates
   */
  const { pageSize = 20, location, params } = props

  const intl = useProcurifyIntl()

  const [lastBranch, setLastBranch] = useState(null)

  const secondaryOrgCategoryPlaceholder = intl.formatMessage(
    messages.secondaryOrgCategorySelectPlaceholder
  )

  const loadOptions: AsyncSelectLoadOptions<Department> = (
    inputValue,
    cachePagination,
    callback
  ) => {
    const url = `${DEPARTMENTS_URL}/`

    ApiClient.get<any, Department[]>(url, {
      params: {
        ...params,
        search: inputValue,
        branch: location,
        order_by: 'branch__name',
        page_size: pageSize,
        page: cachePagination.page,
      },
    }).then((res) => {
      const depts = res.data
      const responsePagination = res.metadata.pagination

      callback({
        options: groupOptions(depts, (dept) => {
          if (dept?.branch.id === lastBranch) {
            return { groupIsInLastPage: true, groupId: dept?.branch.id }
          }
          return {
            groupIsInLastPage: false,
            groupId: dept.branch ? dept.branch.id : '',
          }
        }),
        pagination: {
          hasMore: !!responsePagination.next,
          page: responsePagination.current_page + 1,
        },
      })

      setLastBranch(depts[depts.length - 1]?.branch.id)
    })
  }

  return (
    <DepartmentSelectSelectStyled>
      <AsyncSelect
        cacheOptions={true}
        placeholder={secondaryOrgCategoryPlaceholder}
        loadOptions={loadOptions}
        getOptionLabel={(option) => option.name}
        getOptionValue={(option) => (option ? option.id.toString() : null)}
        {...props}
      />
    </DepartmentSelectSelectStyled>
  )
}
